import React, { useState, Fragment } from 'react';
import { CheckCircle, XCircle, Clock, FileText, User, Check, X } from 'lucide-react';
import { useAppContext } from '../context/AppContext';
export function ApprovalsPage() {
  const {
    travelRequests,
    approveRequest,
    rejectRequest
  } = useAppContext();
  const [activeTab, setActiveTab] = useState('pending');
  const [rejectId, setRejectId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const approvalSteps = [{
    id: 1,
    label: 'Request Submitted',
    status: 'completed'
  }, {
    id: 2,
    label: 'Policy Check',
    status: 'completed'
  }, {
    id: 3,
    label: 'Manager Approval',
    status: 'current'
  }, {
    id: 4,
    label: 'Dept Head',
    status: 'pending'
  }, {
    id: 5,
    label: 'Finance Review',
    status: 'pending'
  }, {
    id: 6,
    label: 'Booking Confirmed',
    status: 'pending'
  }];
  const filteredApprovals = travelRequests.filter(a => a.status === activeTab || activeTab === 'all');
  const pendingCount = travelRequests.filter(r => r.status === 'pending').length;
  const handleApprove = (id: string) => {
    approveRequest(id);
    setSuccessMsg('Request approved successfully');
    setTimeout(() => setSuccessMsg(null), 3000);
  };
  const handleRejectClick = (id: string) => {
    setRejectId(id);
  };
  const confirmReject = () => {
    if (rejectId) {
      rejectRequest(rejectId);
      setRejectId(null);
      setRejectReason('');
      setSuccessMsg('Request rejected');
      setTimeout(() => setSuccessMsg(null), 3000);
    }
  };
  return <div className="space-y-6" data-id="element-224">
      {successMsg && <div className="fixed top-20 right-6 z-50 animate-in slide-in-from-top-2" data-id="element-225">
          <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-lg p-4 shadow-lg flex items-center gap-3" data-id="element-226">
            <CheckCircle className="h-5 w-5 text-emerald-600" data-id="element-227" />
            <p className="font-medium" data-id="element-228">{successMsg}</p>
          </div>
        </div>}

      <div className="flex items-center justify-between" data-id="element-229">
        <div data-id="element-230">
          <h2 className="text-2xl font-bold text-slate-800" data-id="element-231">Trip Approvals</h2>
          <p className="text-slate-500" data-id="element-232">
            Review and manage travel requests from your team.
          </p>
        </div>
      </div>

      {/* Approval Process Visualization (Generic or based on first pending) */}
      <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm overflow-x-auto" data-id="element-233">
        <h3 className="text-sm font-semibold text-slate-800 mb-4" data-id="element-234">
          Standard Approval Workflow
        </h3>
        <div className="flex items-center min-w-[700px]" data-id="element-235">
          {approvalSteps.map((step, idx) => <Fragment key={step.id} data-id="element-236">
              <div className="flex flex-col items-center relative z-10" data-id="element-237">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-all ${step.status === 'completed' ? 'bg-emerald-500 border-emerald-500 text-white' : step.status === 'current' ? 'bg-white border-blue-600 text-blue-600 ring-4 ring-blue-50' : 'bg-white border-slate-200 text-slate-400'}`} data-id="element-238">
                  {step.status === 'completed' ? <Check className="w-4 h-4" data-id="element-239" /> : step.id}
                </div>
                <span className={`text-xs font-medium mt-2 absolute -bottom-6 w-32 text-center ${step.status === 'current' ? 'text-blue-600' : 'text-slate-500'}`} data-id="element-240">
                  {step.label}
                </span>
              </div>
              {idx < approvalSteps.length - 1 && <div className={`flex-1 h-0.5 mx-2 ${step.status === 'completed' ? 'bg-emerald-500' : 'bg-slate-200'}`} data-id="element-241" />}
            </Fragment>)}
        </div>
        <div className="h-6" data-id="element-242"></div>
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-200" data-id="element-243">
        <nav className="-mb-px flex space-x-8" data-id="element-244">
          {['pending', 'approved', 'rejected', 'all'].map(tab => <button key={tab} onClick={() => setActiveTab(tab)} className={`
                whitespace-nowrap border-b-2 py-4 px-1 text-sm font-medium capitalize
                ${activeTab === tab ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-700'}
              `} data-id="element-245">
              {tab}{' '}
              {tab === 'pending' && pendingCount > 0 && <span className="ml-2 rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800" data-id="element-246">
                  {pendingCount}
                </span>}
            </button>)}
        </nav>
      </div>

      {/* Approval List */}
      <div className="space-y-4" data-id="element-247">
        {filteredApprovals.length === 0 ? <div className="text-center py-12 bg-white rounded-xl border border-slate-200" data-id="element-248">
            <p className="text-slate-500" data-id="element-249">No requests found.</p>
          </div> : filteredApprovals.map(request => <div key={request.id} className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm transition-all hover:shadow-md" data-id="element-250">
              <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between" data-id="element-251">
                {/* Requester Info */}
                <div className="flex gap-4 lg:w-1/4" data-id="element-252">
                  <div className="h-12 w-12 flex-shrink-0 rounded-full bg-slate-100 flex items-center justify-center text-slate-500" data-id="element-253">
                    <User className="h-6 w-6" data-id="element-254" />
                  </div>
                  <div data-id="element-255">
                    <h3 className="font-semibold text-slate-900" data-id="element-256">
                      {request.requester}
                    </h3>
                    <p className="text-sm text-slate-500" data-id="element-257">{request.role}</p>
                    <div className="mt-2 flex items-center text-xs text-slate-500" data-id="element-258">
                      <Clock className="mr-1 h-3.5 w-3.5" data-id="element-259" />
                      Submitted {request.submittedAt}
                    </div>
                  </div>
                </div>

                {/* Trip Details */}
                <div className="flex-1 space-y-4 lg:px-6" data-id="element-260">
                  <div data-id="element-261">
                    <div className="flex items-center justify-between" data-id="element-262">
                      <h4 className="font-medium text-slate-900" data-id="element-263">
                        {request.trip}
                      </h4>
                      <span className="font-bold text-slate-900" data-id="element-264">
                        ${request.amount}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-4 text-sm text-slate-600 mt-2" data-id="element-265">
                      <div data-id="element-266">
                        <span className="block text-xs text-slate-400" data-id="element-267">
                          Destination
                        </span>
                        {request.destination}
                      </div>
                      <div data-id="element-268">
                        <span className="block text-xs text-slate-400" data-id="element-269">
                          Dates
                        </span>
                        {request.dates}
                      </div>
                    </div>
                  </div>

                  {/* Approval Chain Visualization */}
                  <div className="bg-slate-50 rounded-lg p-3 border border-slate-100" data-id="element-270">
                    <h5 className="text-xs font-semibold text-slate-500 uppercase mb-2" data-id="element-271">
                      Approval Chain
                    </h5>
                    <div className="space-y-2" data-id="element-272">
                      {request.chain.map((step, i) => <div key={i} className="flex items-center justify-between text-sm" data-id="element-273">
                          <div className="flex items-center gap-2" data-id="element-274">
                            <div className={`w-2 h-2 rounded-full ${step.status === 'approved' ? 'bg-emerald-500' : step.status === 'pending' ? 'bg-amber-500' : 'bg-slate-300'}`} data-id="element-275" />
                            <span className="text-slate-600" data-id="element-276">
                              {step.role}:{' '}
                              <span className="font-medium text-slate-900" data-id="element-277">
                                {step.name}
                              </span>
                            </span>
                          </div>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${step.status === 'approved' ? 'bg-emerald-100 text-emerald-700' : step.status === 'pending' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-500'}`} data-id="element-278">
                            {step.status.charAt(0).toUpperCase() + step.status.slice(1)}
                          </span>
                        </div>)}
                    </div>
                  </div>

                  {request.policyFlags && request.policyFlags.length > 0 && <div className="mt-3 rounded-lg bg-amber-50 p-3 text-sm text-amber-800 border border-amber-100" data-id="element-279">
                      <span className="font-semibold" data-id="element-280">Policy Warning:</span>{' '}
                      {request.policyFlags[0]}
                    </div>}
                </div>

                {/* Actions */}
                <div className="flex flex-col gap-3 lg:w-48 lg:border-l lg:border-slate-100 lg:pl-6" data-id="element-281">
                  {request.status === 'pending' ? rejectId === request.id ? <div className="space-y-2 animate-in fade-in" data-id="element-282">
                        <textarea className="w-full text-sm border rounded p-2" placeholder="Reason for rejection..." value={rejectReason} onChange={e => setRejectReason(e.target.value)} rows={2} data-id="element-283" />
                        <div className="flex gap-2" data-id="element-284">
                          <button onClick={confirmReject} className="flex-1 bg-red-600 text-white text-xs py-1.5 rounded hover:bg-red-700" data-id="element-285">
                            Confirm
                          </button>
                          <button onClick={() => setRejectId(null)} className="flex-1 bg-slate-100 text-slate-600 text-xs py-1.5 rounded hover:bg-slate-200" data-id="element-286">
                            Cancel
                          </button>
                        </div>
                      </div> : <>
                        <button onClick={() => handleApprove(request.id)} className="flex items-center justify-center rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700" data-id="element-287">
                          <CheckCircle className="mr-2 h-4 w-4" data-id="element-288" />
                          Approve
                        </button>
                        <button onClick={() => handleRejectClick(request.id)} className="flex items-center justify-center rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50" data-id="element-289">
                          <XCircle className="mr-2 h-4 w-4" data-id="element-290" />
                          Reject
                        </button>
                      </> : <div className={`flex items-center justify-center rounded-lg px-4 py-2 text-sm font-medium ${request.status === 'approved' ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'}`} data-id="element-291">
                      {request.status === 'approved' ? 'Approved' : 'Rejected'}
                    </div>}
                  <button className="flex items-center justify-center text-sm text-blue-600 hover:underline" data-id="element-292">
                    <FileText className="mr-1 h-4 w-4" data-id="element-293" />
                    View Details
                  </button>
                </div>
              </div>
            </div>)}
      </div>
    </div>;
}